// Pattern Studio — public barrel.
//
// CANONICAL ARCHITECTURE — one render path, two creation paths
// ────────────────────────────────────────────────────────────
// Every entity (market, profile, outcome, etc.) ends up with a single
// PNG on remote storage (Pinata / IPFS / S3). The lib's role at display
// time is identical regardless of how that PNG got there: fetch it,
// sample brightness, apply the lib's CURRENT palette + dither + cellSize
// + algorithm, paint to canvas.
//
// Two ways the source PNG is created:
//
//   • USER-UPLOADED — avatars, market images, outcome images. The user
//     uploads their photo. The "shape" is whatever they uploaded.
//
//   • CLIENT-GENERATED — profile cover images. At creation time the
//     client calls `generateGenerativePng(variant, seed, …)` to render
//     the variant's brightness field as a smooth grayscale PNG, then
//     uploads that to Pinata. Variant + seed are LOCKED into the PNG;
//     formatting (palette, dither, cellSize, algorithm) reapplies on
//     every render so lib changes still propagate.
//
// PROPAGATION CONTRACT
// ────────────────────
// What you change in the lib that updates EVERY existing entity:
//   • Brand / outcome CSS token hex values
//   • `PRESET_TOKENS_RAW` step lists
//   • `DEFAULT_PARAMS` numeric defaults (cellSize, mBrightness, etc.)
//   • `DEFAULT_OUTCOME_STEPS`
//   • The mosaic dither algorithm in `renderers/mosaic.ts`
//
// What's locked per-entity:
//   • The source PNG (Pinata URL)
//   • For client-generated covers: the variant + seed inside the PNG
//   • The palette ref the consumer chose
//
// To pick up a brightness-function improvement on existing covers,
// regenerate + re-upload — there's no "everyone gets the new wave shape
// for free" path because the shape is baked into the PNG.

// ── Bake helpers — workspace export + optional fallback previews ──────
export { type BakeAndDownloadOptions, bakeAndDownloadPattern } from './bake/bake-and-download';
export { bakePatternToBlob } from './bake/bake-to-blob';
export { downloadBlob } from './bake/download';
// ── Client surface (React) — drop-ins for consumer surfaces ──────────
export {
  PatternCanvas,
  type PatternCanvasProps,
  type PatternCanvasState,
  type UsePatternCanvasOptions,
  type UsePatternCanvasResult,
  usePatternCanvas,
} from './client';
// ── Pure helpers ──────────────────────────────────────────────────────
export {
  type BrightnessFn,
  brightnessBloom,
  brightnessNoise,
  brightnessRings,
  brightnessStripes,
  brightnessWaves,
  buildVariantContext,
  getBrightnessFn,
  VARIANT_FNS,
  type VariantContext,
} from './core/brightness-fns';
export {
  cachedLoadImageFromUrl,
  clearImageCache,
  invalidateImageCache,
} from './core/browser/image-cache';
export { loadImageFile } from './core/browser/image-loader';
export {
  resolveColorToken,
  resolveCssColor,
  resolvePaletteFromTokens,
} from './core/browser/token-resolver';
export { loadImageFromUrl } from './core/browser/url-loader';
export { hexToRgb, isValidHex, normalizeHex, sampleLUT } from './core/color';
export { hashStringToSeed, pickFromHash } from './core/derive';
export {
  ALL_GENERATIVE_VARIANTS,
  type AspectName,
  buildDensityConfig,
  buildDotRenderConfig,
  type DensityConfigOverrides,
  DOT_PALETTE_NAMES,
  DOT_PALETTES,
  DOT_STUDIO_MODES,
  type DotCoverOverrides,
  type DotPalette,
  type DotPaletteName,
  type DotRenderConfig,
  type DotStudioMode,
  dotPaletteColors,
  dotStudioModeLabel,
  dotStudioPatternForMode,
  encodeDotStudioHeroMarker,
  type FadeMode,
  type GridType,
  generateHeroCoverPickerOptions,
  HERO_COVER_ALT_MODES,
  HERO_COVER_VARIANTS,
  heroCoverDisplayMode,
  heroCoverForUser,
  isDotPaletteName,
  isDotStudioMode,
  isLegacyGenerativeVariant,
  LEGACY_GENERATIVE_VARIANTS,
  type MarkVariant,
  PATTERN_GROUP_LABELS,
  PATTERN_GROUP_ORDER,
  PATTERN_GROUPS,
  PATTERN_PICKER_MODES,
  type PatternGroupId,
  parseDotStudioHeroMarker,
  pickColor,
  type RandomDotStudioPattern,
  randomizeDotStudioPattern,
  randomMorfiSeedText,
  renderDotStudioCover,
  resolveHeroCoverPattern,
  rngFromSeed,
  SOURCE_PRESET_PARAMS,
} from './core/dot-engine';
export {
  type CoverPngMimeType,
  type GenerateCoverPngOptions,
  generateDotStudioCoverPng,
} from './core/generate-cover-png';
export {
  type GenerateGenerativePngOptions,
  type GenerativePngMimeType,
  generateGenerativePng,
} from './core/generate-png';
export { sampleGenerativeBrightness } from './core/generative-brightness';
export { averageBrightness, averageColor, sampleImageBrightness } from './core/image-sample';
export { makeRng, type Rng, rngOnce } from './core/rng';
export { shapeMask } from './core/shape-mask';
export { applyPalette, randomSeed } from './core/state-helpers';
export {
  type DecodedManifestHash,
  decodeManifestHash,
  encodeManifestHash,
  halftoneImgModeForTheme,
  MANIFEST_VERSION,
  type ManifestFallbackEvent,
  type ManifestSource,
  type ManifestThemeName,
  type ManifestToStateOverrides,
  type ManifestVersion,
  manifestForMarket,
  manifestForOutcome,
  manifestForUser,
  manifestForUserCover,
  manifestForWinner,
  manifestFromState,
  manifestsEqual,
  manifestToState,
  type PaletteRef,
  type PatternManifest,
  parsePatternManifest,
  type RenderManifestOptions,
  type RenderManifestToBlobUrlOptions,
  rendererForPalette,
  renderManifest,
  renderManifestToBlob,
  resolvePaletteRef,
  synthesiseSampledImage,
} from './manifest';
// ── Presets + tokens ──────────────────────────────────────────────────
export {
  AVATAR_PLACEHOLDER_VARIANT,
  BRAND_BLUE_PALETTES,
  brandRampTokens,
  CATEGORY_PALETTE_MAP,
  CATEGORY_PLACEHOLDER_SOURCE,
  COVER_PLACEHOLDER_VARIANT,
  createPatternState,
  DEFAULT_CATEGORY_PLACEHOLDER,
  DEFAULT_MOSAIC_PARAMS,
  DEFAULT_OUTCOME_STEPS,
  DEFAULT_PALETTE,
  DEFAULT_PALETTE_NAME,
  DEFAULT_PARAMS,
  DEFAULT_STATE,
  OUTCOME_HUES,
  OUTCOME_PLACEHOLDER_VARIANT,
  type OutcomeHueName,
  type OutcomePaletteStep,
  outcomeRampTokens,
  PALETTE_NAMES,
  PALETTES,
  type PaletteName,
  type PlaceholderSource,
  pickBrandBluePalette,
  RECOMMENDED_DARK_PALETTE_NAME,
  RECOMMENDED_LIGHT_PALETTE_NAME,
  resolveBrandPalette,
  resolveCategoryPlaceholderSource,
  resolveOutcomePalette,
} from './presets';
// ── Renderer + types ──────────────────────────────────────────────────
export { renderPattern } from './renderers';
// ── Internal Pattern Studio authoring contract ───────────────────────
export {
  ADVANCED_MODE_GROUPS,
  ADVANCED_MODE_IDS,
  BRAND_PALETTE_NAMES,
  CANVAS_PRESETS,
  isCanvasPresetConsistent,
  MAX_CANVAS_PIXELS,
  MAX_CANVAS_SIDE,
  MIN_CANVAS_SIDE,
  PRIMARY_MODE_IDS,
  selectCanvasPreset,
  swapCanvasOrientation,
  validateCanvasDimensions,
} from './studio/catalog';
export {
  createDefaultGenerativeControls,
  createDefaultImageFrameMode,
  createDefaultMosaicMode,
  createDefaultPatternConfiguration,
  parsePatternConfiguration,
  serializePatternConfiguration,
} from './studio/configuration';
export {
  emitStudioFailure,
  mapStudioFailureRecord,
  STUDIO_FAILURE_CODES,
} from './studio/observability';
export { mapRampToDotColors, resolveStudioPalette } from './studio/palette';
export type {
  ActiveSourceImage,
  BackgroundSelection,
  BrandPaletteName,
  CanonicalImageMediaType,
  CanvasPresetId,
  CanvasSelection,
  CropAnchor,
  GenerativeControls,
  ImageFrameControls,
  MosaicControls,
  OutcomeHue,
  OutputColorMode,
  PaletteSelection,
  PatternConfigurationV1,
  PatternModeConfiguration,
  Result,
  StudioFailure,
  StudioFailureCode,
} from './studio/types';
export type {
  ColorMode,
  EdgeMode,
  GenerativeVariant,
  HalftoneGridType,
  HalftoneImgMode,
  Hex,
  MosaicConfig,
  MosaicParams,
  Palette,
  PatternImage,
  PatternParams,
  PatternRendererKind,
  PatternState,
  Render2DContext,
  RenderFn,
  RgbTriplet,
  SampledImage,
  ShapeMaskKind,
} from './types';
